<template>
  <Button
    size="icon"
    :variant="triggerVariant"
    class="h-9 w-9 shrink-0 rounded-full"
    @click="toggleTheme"
  >
    <Moon v-if="theme === 'light'" :size="18" />
    <Sun v-else :size="18" />
  </Button>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { Button } from '@/shared/components/ui/button'
import { Moon, Sun } from 'lucide-vue-next'

withDefaults(
  defineProps<{
    /** `ghost` matches Shadcn UI Kit dashboard header icon row */
    triggerVariant?: 'outline' | 'ghost'
  }>(),
  { triggerVariant: 'outline' }
)

const theme = ref('dark')

const applyTheme = (newTheme: string) => {
  const root = window.document.documentElement
  root.classList.remove('light', 'dark')
  
  root.classList.add(newTheme)
  
  const themeColor = newTheme === 'dark' ? '#1e1e1e' : '#fff'
  const metaThemeColor = document.querySelector('meta[name="theme-color"]')
  metaThemeColor?.setAttribute('content', themeColor)
}

const setTheme = (newTheme: string) => {
  theme.value = newTheme
  localStorage.setItem('theme', newTheme)

  const root = window.document.documentElement
  root.classList.add('theme-transition')
  window.requestAnimationFrame(() => {
    applyTheme(newTheme)
  })
  window.setTimeout(() => {
    root.classList.remove('theme-transition')
  }, 160)
}

const toggleTheme = () => {
  const newTheme = theme.value === 'light' ? 'dark' : 'light'
  setTheme(newTheme)
}

onMounted(() => {
  const storedTheme = localStorage.getItem('theme')
  if (storedTheme) {
    setTheme(storedTheme)
  } else {
    setTheme('dark')
  }
})
</script>
